import { useEffect, useRef, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, UPLOADS_BASE, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Upload, Pencil, Scale } from "lucide-react";
import { toast } from "sonner";

// Formulaire du site restreint aux champs encore modifiables depuis ici
// (demande explicite : contrôle centralisé depuis le panel d'hébergement
// pour nom, adresse, code postal, ville, N° SIRET, modèle de vente, fond de
// caisse, attestation Volet 2, statut actif et SMTP — tous retirés de ce
// formulaire de saisie, pour qu'ils ne soient JAMAIS réémis dans un PATCH
// depuis le site, même à l'identique : voir save() et le commentaire sur
// /api/shops côté backend, où ces champs restent pleinement fonctionnels et
// modifiables via le panel, qui passe par la même API avec les identifiants
// admin du site. Nom, adresse, code postal, ville et N° SIRET restent
// affichés dans la boîte de dialogue (demande explicite), mais en lecture
// seule — voir les champs grisés juste après le DialogTitle, qui lisent
// directement `editing` et non `form`, pour qu'ils ne puissent jamais se
// retrouver dans le payload envoyé).
const EMPTY_FORM = {
  type: "boutique", telephone: "",
  tva_intracom: "", forme_juridique: "", capital_social: "", rcs_ville: "", tva_sur_debits: false,
  ticket_impression_auto: false, activite_exemptee_agec: false,
};

export default function ShopSettingsPage({ embedded = false }) {
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const logoRefs = useRef({});

  const load = async () => {
    const { data } = await api.get("/shops");
    setShops(data);
  };
  useEffect(() => { load(); }, []);

  // Création de boutique retirée côté site (demande explicite) : nom,
  // adresse, modèle de vente, fond de caisse, attestation, statut actif et
  // SMTP sont désormais pilotés exclusivement depuis le panel d'hébergement
  // — voir le README de ce dernier, qui pilote déjà /api/shops avec les
  // identifiants admin du site. Seule la modification des champs encore
  // autorisés ci-dessous (téléphone, SIRET, mentions légales, ticket AGEC,
  // logo) reste possible ici.
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      type: s.type, telephone: s.telephone,
      tva_intracom: s.tva_intracom || "", forme_juridique: s.forme_juridique || "",
      capital_social: s.capital_social || "", rcs_ville: s.rcs_ville || "", tva_sur_debits: !!s.tva_sur_debits,
      ticket_impression_auto: !!s.ticket_impression_auto, activite_exemptee_agec: !!s.activite_exemptee_agec,
    });
    setOpen(true);
  };

  // Toujours une modification désormais (plus de création côté site). Le
  // formulaire ne contient plus QUE les champs encore éditables ici (voir
  // EMPTY_FORM/openEdit ci-dessus) : nom, adresse, modèle de vente, fond de
  // caisse, attestation Volet 2, statut actif et SMTP n'y figurent plus du
  // tout, donc ce PATCH ne peut jamais les réécrire — même par erreur,
  // même à l'identique (crucial pour smtp_password en particulier, qui
  // écraserait silencieusement le mot de passe existant par une valeur
  // vide s'il était réémis ici sans être rempli).
  const save = async () => {
    try {
      await api.patch(`/shops/${editing.id}`, form);
      toast.success("Boutique enregistrée");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadLogo = async (shopId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/shops/${shopId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Logo mis à jour");
    load();
  };

  const body = (
    <>
      {/* Bouton de création retiré (demande explicite) : une nouvelle
          boutique ne se crée plus que depuis le panel d'hébergement. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="shop-list">
        {shops.map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-card p-5" data-testid={`shop-card-${s.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {s.logo_url ? <img src={`${UPLOADS_BASE}${s.logo_url}`} className="w-12 h-12 object-contain rounded-md bg-secondary" /> : <div className="w-12 h-12 rounded-md bg-secondary" />}
                <div>
                  <p className="font-heading font-semibold flex items-center gap-2">
                    {s.nom}
                    {s.actif === false && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-500/15 text-slate-500 font-medium uppercase tracking-wide">Inactive</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">{s.type}</p>
                </div>
              </div>
              <button data-testid={`shop-edit-${s.id}`} onClick={() => openEdit(s)}><Pencil size={15} /></button>
            </div>
            <p className="text-sm text-muted-foreground">{s.adresse}</p>
            {(s.code_postal || s.ville) && (
              <p className="text-sm text-muted-foreground">{[s.code_postal, s.ville].filter(Boolean).join(" ")}</p>
            )}
            <p className="text-sm text-muted-foreground">Tél: {s.telephone}</p>
            {s.siret && <p className="text-xs text-muted-foreground mb-2">SIRET: {s.siret}</p>}
            <input type="file" accept="image/*" className="hidden" ref={(el) => (logoRefs.current[s.id] = el)} onChange={(e) => e.target.files[0] && uploadLogo(s.id, e.target.files[0])} data-testid={`shop-logo-input-${s.id}`} />
            <Button size="sm" variant="outline" className="gap-2" onClick={() => logoRefs.current[s.id].click()} data-testid={`shop-logo-button-${s.id}`}><Upload size={14} />Changer le logo</Button>
          </div>
        ))}
      </div>

      {/* Nom, adresse, code postal, ville, modèle de l'onglet Vente, fond de
          caisse, attestation Volet 2, statut actif et SMTP ont été retirés de
          ce formulaire (demande explicite) : ils restent pleinement
          fonctionnels côté produit, mais ne sont plus modifiables QUE depuis
          le panel d'hébergement, qui pilote /api/shops avec les identifiants
          admin du site. Le nom et l'adresse restent affichés en lecture
          seule sur la carte de la boutique ci-dessus (inchangé). */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shop-form-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier {editing?.nom}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Nom, adresse, code postal, ville et N° SIRET : affichés
                (demande explicite) mais non modifiables depuis le site —
                pilotés uniquement depuis le panel d'hébergement. Lisent
                directement `editing`, jamais `form`, pour ne jamais pouvoir
                être réémis dans le PATCH de save(). */}
            <Input data-testid="shop-form-nom" placeholder="Nom" value={editing?.nom || ""} disabled />
            <Input data-testid="shop-form-adresse" placeholder="Adresse" value={editing?.adresse || ""} disabled />
            <div className="grid grid-cols-3 gap-3">
              <Input data-testid="shop-form-code-postal" placeholder="Code postal" className="col-span-1" value={editing?.code_postal || ""} disabled />
              <Input data-testid="shop-form-ville" placeholder="Ville" className="col-span-2" value={editing?.ville || ""} disabled />
            </div>
            <Input data-testid="shop-form-siret" placeholder="N° SIRET" value={editing?.siret || ""} disabled />

            {/* Type non modifiable de toute façon (disabled) : conservé tel
                quel, simple rappel visuel de la nature de cette fiche. */}
            <Select value={form.type} disabled>
              <SelectTrigger data-testid="shop-form-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="boutique">Boutique</SelectItem>
                <SelectItem value="depot">Dépôt</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="shop-form-telephone" placeholder="Téléphone" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Scale size={15} />Mentions légales (factures)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Obligatoires sur toute facture dépassant 150 € HT ou concernant une société (art. 242 nonies A du CGI).
                Laissez vide si non applicable (ex : entrepreneur individuel sans capital social).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="shop-form-tva-intracom" placeholder="N° TVA intracommunautaire" value={form.tva_intracom} onChange={(e) => setForm({ ...form, tva_intracom: e.target.value })} />
                <Input data-testid="shop-form-rcs-ville" placeholder="Ville du RCS" value={form.rcs_ville} onChange={(e) => setForm({ ...form, rcs_ville: e.target.value })} />
                <Input data-testid="shop-form-forme-juridique" placeholder="Forme juridique (ex: SARL)" value={form.forme_juridique} onChange={(e) => setForm({ ...form, forme_juridique: e.target.value })} />
                <Input data-testid="shop-form-capital-social" placeholder="Capital social (ex: 10 000 €)" value={form.capital_social} onChange={(e) => setForm({ ...form, capital_social: e.target.value })} />
              </div>
              <label className="flex items-center gap-2.5 mt-3 cursor-pointer" htmlFor="shop-form-tva-debits">
                <Checkbox id="shop-form-tva-debits" data-testid="shop-form-tva-debits" checked={form.tva_sur_debits} onCheckedChange={(v) => setForm({ ...form, tva_sur_debits: !!v })} />
                <span className="text-sm">Option pour le paiement de la TVA d'après les débits</span>
              </label>
            </div>

            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Scale size={15} />Impression du ticket de caisse (loi AGEC)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Depuis le 1ᵉʳ août 2023 (art. L541-15-10 du code de l'environnement), le ticket de caisse ne doit
                plus être imprimé/remis automatiquement à chaque vente — seulement à la demande du client, sauf
                activité exemptée (hôtellerie-restauration notamment).
              </p>
              <label className="flex items-center gap-2.5 cursor-pointer" htmlFor="shop-form-agec-exempt">
                <Checkbox id="shop-form-agec-exempt" data-testid="shop-form-agec-exempt" checked={form.activite_exemptee_agec} onCheckedChange={(v) => setForm({ ...form, activite_exemptee_agec: !!v, ticket_impression_auto: !!v || form.ticket_impression_auto })} />
                <span className="text-sm">Activité exemptée par la loi (hôtellerie, restauration...)</span>
              </label>
              <label className="flex items-center gap-2.5 mt-2 cursor-pointer" htmlFor="shop-form-ticket-auto">
                <Checkbox
                  id="shop-form-ticket-auto"
                  data-testid="shop-form-ticket-auto"
                  checked={form.ticket_impression_auto}
                  disabled={form.activite_exemptee_agec}
                  onCheckedChange={(v) => setForm({ ...form, ticket_impression_auto: !!v })}
                />
                <span className="text-sm">Ouvrir/imprimer le ticket automatiquement après chaque vente</span>
              </label>
              {!form.ticket_impression_auto && !form.activite_exemptee_agec && (
                <p className="text-xs text-muted-foreground mt-1">
                  Réglage recommandé par défaut : le vendeur affichera un bouton « Imprimer » à la caisse, à
                  utiliser seulement si le client le demande.
                </p>
              )}
            </div>

            {/* Modèle de l'onglet Vente, Fond de caisse, Attestation Volet 2,
                Boutique active et SMTP : retirés (demande explicite),
                désormais pilotés uniquement depuis le panel d'hébergement —
                voir le commentaire au-dessus du Dialog. */}
          </div>
          <DialogFooter><Button data-testid="shop-form-save" onClick={save}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) return body;
  return <Layout title="Boutiques &amp; Dépôts">{body}</Layout>;
}
